// Formateadores compartidos de la vista de reportes del helpdesk.
// es-CL en todos: coma decimal y punto de miles, igual que el resto del portal.

const nfDecimal = new Intl.NumberFormat("es-CL", { maximumFractionDigits: 1 });
const nfInt = new Intl.NumberFormat("es-CL");

/**
 * Duración legible a partir de minutos (el backend ya redondea a 1 decimal).
 * Bajo 90 min se lee mejor en minutos ("42 min"); desde ahí, en horas ("2,3 h").
 * null/undefined significa "sin datos suficientes en el rango" y se muestra
 * como raya, no como 0: cero sería una métrica falsa.
 * @param {number|null|undefined} min
 * @returns {string}
 */
export function formatMinutes(min) {
  if (min === null || min === undefined || Number.isNaN(Number(min))) return "—";
  const value = Number(min);
  if (value < 90) return `${nfDecimal.format(value)} min`;
  return `${nfDecimal.format(value / 60)} h`;
}

/**
 * Entero con separador de miles chileno. Los conteos pueden venir undefined
 * cuando el backend aún no está desplegado (vista navegable vacía).
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function formatInt(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return nfInt.format(Number(value));
}

/**
 * Ticks numéricos de eje Y. Compacta los miles ("1200" → "1,2 k") porque los
 * ejes de recharts van con width fijo (40px) y un valor de 4+ dígitos se
 * RECORTABA por la izquierda: "1200" se leía "200" y el gráfico mentía.
 * Compactar en vez de ensanchar mantiene el área de trazado pareja entre
 * gráficos apilados en la misma grilla.
 * @param {number} value
 * @returns {string}
 */
export function formatAxisCount(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  if (Math.abs(n) < 1000) return String(n);
  // Sin espacio antes de la k: los gráficos van con margin left negativo y
  // esos ~4px eran la diferencia entre "1,2k" y un "1" decapitado.
  return `${(n / 1000).toLocaleString("es-CL", { maximumFractionDigits: 1 })}k`;
}

/**
 * Fecha+hora corta para las celdas del drill-down de casos ("15-07 14:30",
 * hora LOCAL del operador). null/undefined/basura → raya: un caso vivo no
 * tiene resolved_at y eso es información, no un error.
 * @param {string|null|undefined} iso
 * @returns {string}
 */
export function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  // hour12 explícito en false: algunos ICU resuelven es-CL con "a. m./p. m."
  // y el sufijo engordaba las columnas de fecha hasta forzar scroll lateral.
  return d.toLocaleString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * Fecha local como "YYYY-MM-DD" SIN pasar por toISOString(): en Chile (UTC-3/-4)
 * el ISO UTC corre el día y descuadraría el relleno de días contra las claves
 * `date` que manda el backend.
 * @param {Date} d
 * @returns {string}
 */
export function localYMD(d) {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * "YYYY-MM-DD" → Date en medianoche LOCAL. `new Date("YYYY-MM-DD")` interpreta
 * UTC y en Chile devuelve el día anterior; el sufijo T00:00:00 fuerza local.
 * @param {string} ymd
 * @returns {Date}
 */
export function ymdToLocalDate(ymd) {
  return new Date(`${ymd}T00:00:00`);
}

/**
 * Rellena los días faltantes de por_dia con ceros. El backend manda solo los
 * días con actividad; sin relleno, la línea del gráfico "saltaría" fines de
 * semana e induciría a leer continuidad donde hubo silencio. Itera en fechas
 * LOCALES (localYMD) porque las claves `date` son días locales del tenant.
 * Vive aquí (y no en el componente del chart) para poder testearlo sin
 * arrastrar recharts al entorno de jest.
 * @param {Array<{date: string, inbound: number, outbound: number}>} rows
 * @param {string} fromYMD
 * @param {string} toYMD
 * @returns {Array<{date: string, inbound: number, outbound: number}>}
 */
export function fillDays(rows, fromYMD, toYMD) {
  if (!fromYMD || !toYMD) return rows || [];
  const byDate = new Map((rows || []).map((r) => [r.date, r]));
  const out = [];
  const cursor = ymdToLocalDate(fromYMD);
  const end = ymdToLocalDate(toYMD);
  // Tope defensivo: el rango ya viene validado a ≤92 días, pero un from/to
  // corrupto no debe colgar el render en un while infinito.
  while (cursor <= end && out.length < 100) {
    const key = localYMD(cursor);
    const row = byDate.get(key);
    out.push({
      date: key,
      inbound: row?.inbound || 0,
      outbound: row?.outbound || 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

/**
 * Rango legible para el subtítulo del header ("1 de julio de 2026 – 22 de
 * julio de 2026").
 * @param {string} fromYMD
 * @param {string} toYMD
 * @returns {string}
 */
export function formatRangeLabel(fromYMD, toYMD) {
  const opts = { day: "numeric", month: "long", year: "numeric" };
  const from = ymdToLocalDate(fromYMD).toLocaleDateString("es-CL", opts);
  const to = ymdToLocalDate(toYMD).toLocaleDateString("es-CL", opts);
  return `${from} – ${to}`;
}
